import type { DrawerMode } from '@monorepo/shared/schemas/common'
import { CHARGE_TYPE, DRAWER_MODE } from '@monorepo/shared/schemas/common'
import type { PlanCharge } from '@monorepo/shared/schemas/plans'
import { Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { toast } from '@/shared/lib/toast'
import { useAddCharge, useUpdateCharge } from '../../hooks/usePlans'
import type {
  CreatePlanCharge,
  UpdatePlanCharge,
} from '../../services/planService'

interface OneTimeChargeFormData {
  charge_name: string
  charge_code: string
  amount: number | null
  description: string
}

interface OneTimeChargeDrawerProps {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  editingCharge?: PlanCharge
  mode?: DrawerMode
}

export function OneTimeChargeDrawer({
  planId,
  open,
  onOpenChange,
  editingCharge,
  mode = DRAWER_MODE.create,
}: OneTimeChargeDrawerProps) {
  const addCharge = useAddCharge()
  const updateCharge = useUpdateCharge()

  const [formData, setFormData] = useState<OneTimeChargeFormData>({
    charge_name: '',
    charge_code: '',
    amount: null,
    description: '',
  })

  // Initialize form when editing
  useEffect(() => {
    if (mode === DRAWER_MODE.edit && editingCharge) {
      setFormData({
        charge_name: editingCharge.charge_name || '',
        charge_code: editingCharge.charge_code || '',
        amount: editingCharge.amount ?? null,
        description: editingCharge.description || '',
      })
    } else {
      setFormData({
        charge_name: '',
        charge_code: '',
        amount: null,
        description: '',
      })
    }
  }, [mode, editingCharge])

  const handleSave = async () => {
    // Validation
    if (!formData.charge_name.trim()) {
      toast.error('Charge name is required')
      return
    }
    if (!formData.charge_code.trim()) {
      toast.error('Charge code is required')
      return
    }
    if (formData.amount === null || formData.amount < 0) {
      toast.error('Amount must be a positive number')
      return
    }

    try {
      const apiData: CreatePlanCharge | UpdatePlanCharge = {
        charge_name: formData.charge_name.trim(),
        charge_code: formData.charge_code.trim(),
        charge_type: CHARGE_TYPE.one_time,
        billing_frequency: null,
        amount: formData.amount,
        description: formData.description.trim() || null,
      }

      if (mode === DRAWER_MODE.create) {
        await addCharge.mutateAsync({
          planId,
          data: apiData as CreatePlanCharge,
        })

        toast.success('One-time charge added successfully')
      } else {
        if (!editingCharge) return

        await updateCharge.mutateAsync({
          planId,
          chargeId: editingCharge.id,
          data: apiData as UpdatePlanCharge,
        })

        toast.success('One-time charge updated successfully')
      }

      onOpenChange(false)
    } catch {
      // Error toast is handled by the mutation hook
    }
  }

  const isSaving = addCharge.isPending || updateCharge.isPending

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[500px] max-w-[700px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {mode === DRAWER_MODE.create
              ? 'Add One-Time Cost'
              : 'Edit One-Time Cost'}
          </DrawerTitle>
          <DrawerDescription>
            Configure a one-time charge that is applied once to this plan
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='space-y-6'>
            {/* Charge Name */}
            <div>
              <Label htmlFor='charge-name'>
                Charge Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='charge-name'
                type='text'
                placeholder='e.g., Setup Fee'
                value={formData.charge_name}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    charge_name: e.target.value,
                  }))
                }
                className='mt-1'
              />
            </div>

            {/* Charge Code */}
            <div>
              <Label htmlFor='charge-code'>
                Charge Code <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='charge-code'
                type='text'
                placeholder='e.g., setup_fee'
                value={formData.charge_code}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    charge_code: e.target.value,
                  }))
                }
                className='mt-1'
              />
              <p className='text-xs text-muted-foreground mt-1'>
                A unique identifier for this charge (lowercase, underscores
                allowed)
              </p>
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor='amount'>
                Amount ($) <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='amount'
                type='number'
                step='0.01'
                min='0'
                placeholder='0.00'
                value={formData.amount ?? ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    amount: e.target.value ? parseFloat(e.target.value) : null,
                  }))
                }
                className='mt-1'
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                placeholder='Optional description of this charge'
                value={formData.description}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={3}
                className='mt-1'
              />
            </div>
          </div>
        </div>

        <DrawerFooter className='border-t'>
          <div className='flex items-center justify-end gap-2'>
            <DrawerClose asChild>
              <Button variant='outline' disabled={isSaving}>
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              {mode === DRAWER_MODE.create ? 'Add Charge' : 'Save Changes'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
